/* -------------------------------------------------------------------------- */
/* CAMERA SCENE ACTIONS                                                       */
/* -------------------------------------------------------------------------- */
/*
 * Cloud Scenes camera actions are written to the write-only IoTS tokens below.
 * The scene backend translates `{ r: 'dev:<id>:<token>', set: { action, params } }`
 * into an RPC call `<component>.<action>` with `params`:
 *   - camera_action      -> camera.<action>   (recording and capture only)
 *   - camerazone_action  -> camerazone.<action>
 *
 * The remaining tokens take a bare value instead of an `{ action, params }` object:
 *   - cam_arm{n} / cam_privacy{n} -> camera.set with the flag as a 'true'/'false' string
 *   - playsound                   -> camera.playsound with `{ id, sound }`
 *
 * `cam_arm{n}` / `cam_privacy{n}` are the same tokens the When side reads, so their
 * suffix carries the {@link CAMERA_ID_OFFSET} channel offset and they are built with
 * {@link buildCameraSceneToken}.
 */

import {
	CAMERA_DURATION_MIN,
	CAMERA_DURATION_MAX,
	CAMERA_DURATION_DEFAULT,
} from 'src/helpers/actions/local/devices/camera_local_actions';
import { isCameraSoundName } from 'src/components/Camera/helpers/camera-sounds';
import {
	buildCameraSceneToken,
	CAMERA_ID_OFFSET,
	CAMERA_SCENE_TOKENS,
	convertCameraUiValueToSceneValue,
	normalizeCameraConditionValue,
} from 'src/components/Scenes/SceneWizard/Process/When/AddEditContionWizard/Steps/DeviceBased/helpers/camera-scene-helpers';

export type CameraActionName = (typeof CAMERA_ACTION_NAMES)[keyof typeof CAMERA_ACTION_NAMES];

export type CameraActionSet = {
	_isdata: true;
	action: string;
	params: Record<string, unknown>;
};

export type CameraActionPayload = {
	token: string;
	/**
	 * An object for the RPC-style tokens, the sound name for `playsound`, and the
	 * 'true'/'false' flag for `cam_arm{n}` / `cam_privacy{n}`.
	 */
	set: CameraActionSet | string;
};

type BuildCameraActionOptions = {
	channel?: number | string;
	zoneId?: number | string;
	duration?: number | string | null;
};

export type CameraActionParseResult = {
	name: CameraActionName;
	value: string;
	channel: number;
	add_info_ind?: string;
	duration?: number;
};

export const CAMERA_ACTION_TOKENS = {
	CAMERA: 'camera_action',
	ZONE: 'camerazone_action',
	PLAY_SOUND: 'playsound',
} as const;

/**
 * UI-level action names. These are matched by the action label/i18n switches and
 * by the camera action wizard component; they never reach the device directly.
 */
export const CAMERA_ACTION_NAMES = {
	CAPTURE: 'camera_capture',
	RECORD: 'camera_record',
	ARM: 'camera_arm',
	PRIVACY: 'camera_privacy',
	ZONE: 'camera_zone',
	PLAY_SOUND: 'camera_play_sound',
} as const;

/**
 * StartRecording duration bounds (seconds). Reuses the local-actions constants
 * (`CAMERA_DURATION_*`) so the scenes UI and local actions stay in sync.
 */
export const CAMERA_RECORD_DURATION = {
	MIN: CAMERA_DURATION_MIN,
	MAX: CAMERA_DURATION_MAX,
	DEFAULT: CAMERA_DURATION_DEFAULT,
} as const;

/**
 * Build the scene `{ token, set }` payload for a UI camera action.
 * `set` follows the object shape `{ _isdata, action, params }` consumed by the backend,
 * except for `playsound` and the arm/privacy tokens, which take a bare value.
 */
export const buildCameraActionSet = (
	name: string,
	value: unknown,
	opts: BuildCameraActionOptions = {},
): CameraActionPayload | null => {
	const channelId = Number(opts.channel ?? 0) || 0;

	switch (name) {
		case CAMERA_ACTION_NAMES.CAPTURE:
			return {
				token: CAMERA_ACTION_TOKENS.CAMERA,
				set: {
					_isdata: true,
					action: 'CaptureImage',
					params: { id: channelId, stream: 0 },
				},
			};

		case CAMERA_ACTION_NAMES.RECORD: {
			const params: Record<string, unknown> = { id: channelId };
			const duration = Number(opts.duration);
			if (opts.duration != null && Number.isFinite(duration) && duration > 0) {
				params.duration = duration;
			}
			return {
				token: CAMERA_ACTION_TOKENS.CAMERA,
				set: { _isdata: true, action: 'StartRecording', params },
			};
		}

		case CAMERA_ACTION_NAMES.ARM:
		case CAMERA_ACTION_NAMES.PRIVACY: {
			const token = buildCameraSceneToken(name, channelId);
			if (!token) {
				return null;
			}
			// The backend applies these only when the flag is the string 'true' / 'false',
			// even though the same tokens carry a real boolean when read as conditions.
			return { token, set: normalizeCameraConditionValue(value) };
		}

		case CAMERA_ACTION_NAMES.PLAY_SOUND:
			return isCameraSoundName(value)
				? { token: CAMERA_ACTION_TOKENS.PLAY_SOUND, set: value }
				: null;

		case CAMERA_ACTION_NAMES.ZONE:
			return {
				token: CAMERA_ACTION_TOKENS.ZONE,
				set: {
					_isdata: true,
					action: 'SetConfig',
					params: {
						id: Number(opts.zoneId ?? 0) || 0,
						config: { enable: convertCameraUiValueToSceneValue(String(value)) },
					},
				},
			};

		default:
			return null;
	}
};

/** Matches the arm/privacy tokens the When and Do sides share, e.g. `cam_arm1`. */
const CAMERA_ARM_PRIVACY_TOKEN = new RegExp(
	`^(${CAMERA_SCENE_TOKENS.ARM}|${CAMERA_SCENE_TOKENS.PRIVACY})(\\d+)$`,
);

/**
 * Whether a scene command token is a camera action token.
 */
export const isCameraSceneActionCommand = (token: string): boolean =>
	token === CAMERA_ACTION_TOKENS.CAMERA ||
	token === CAMERA_ACTION_TOKENS.ZONE ||
	token === CAMERA_ACTION_TOKENS.PLAY_SOUND ||
	CAMERA_ARM_PRIVACY_TOKEN.test(token);

/**
 * Whether a UI action name belongs to the camera action set.
 */
export const isCameraActionName = (name: string): boolean =>
	(Object.values(CAMERA_ACTION_NAMES) as string[]).includes(name);

/**
 * Read arm/privacy back from the `cam_arm{n}` / `cam_privacy{n}` tokens.
 *
 * These are shared with the conditions side, so this returns the same fields
 * {@link parseCameraSceneCommand} does for the very same token.
 */
const parseCameraArmPrivacyToken = (token: string, set: unknown): CameraActionParseResult | null => {
	const [, prefix, id] = token.match(CAMERA_ARM_PRIVACY_TOKEN) ?? [];
	if (!prefix) {
		return null;
	}

	return {
		name: prefix === CAMERA_SCENE_TOKENS.ARM ? CAMERA_ACTION_NAMES.ARM : CAMERA_ACTION_NAMES.PRIVACY,
		value: normalizeCameraConditionValue(set),
		channel: Number(id) - CAMERA_ID_OFFSET,
	};
};

/**
 * Read arm/privacy back from a `camera_action` payload.
 *
 * @deprecated LEGACY_CAMERA_ARM_PRIVACY - these are written to `cam_arm{n}` /
 * `cam_privacy{n}` now, so this only exists to keep already-saved scenes editable.
 * Saved scenes carry the `SetConfig` shape with the flags nested in `config`; the
 * top-level `Set` shape never shipped beyond this MR. Drop this once no stored scene
 * writes arm or privacy through `camera_action`.
 */
const parseLegacyCameraArmPrivacyAction = (
	action: string,
	params: Record<string, any>,
): CameraActionParseResult | null => {
	if (action !== 'setconfig' && action !== 'set') {
		return null;
	}

	const flags = { ...(params.config || {}), ...params };
	const channel = Number(params.id ?? 0) || 0;

	if (typeof flags.arm !== 'undefined') {
		return {
			name: CAMERA_ACTION_NAMES.ARM,
			value: normalizeCameraConditionValue(flags.arm),
			channel,
		};
	}

	if (typeof flags.privacy !== 'undefined') {
		return {
			name: CAMERA_ACTION_NAMES.PRIVACY,
			value: normalizeCameraConditionValue(flags.privacy),
			channel,
		};
	}

	return null;
};

/**
 * Reverse of {@link buildCameraActionSet}: turn a scene `{ token, set }` back into UI fields.
 */
export const parseCameraSceneActionCommand = (
	token: string,
	set: unknown,
): CameraActionParseResult | null => {
	if (token === CAMERA_ACTION_TOKENS.PLAY_SOUND) {
		return isCameraSoundName(set)
			? { name: CAMERA_ACTION_NAMES.PLAY_SOUND, value: set, channel: 0 }
			: null;
	}

	const armPrivacy = parseCameraArmPrivacyToken(token, set);
	if (armPrivacy) {
		return armPrivacy;
	}

	if (!set || typeof set !== 'object') {
		return null;
	}

	const data = set as { action?: string; params?: Record<string, any> };
	const action = String(data.action || '').toLowerCase();
	const params = data.params || {};

	if (token === CAMERA_ACTION_TOKENS.CAMERA) {
		if (action === 'captureimage') {
			return {
				name: CAMERA_ACTION_NAMES.CAPTURE,
				value: 'capture',
				channel: Number(params.id ?? 0) || 0,
			};
		}

		if (action === 'startrecording') {
			return {
				name: CAMERA_ACTION_NAMES.RECORD,
				value: 'record',
				channel: Number(params.id ?? 0) || 0,
				...(params.duration != null && { duration: Number(params.duration) }),
			};
		}

		return parseLegacyCameraArmPrivacyAction(action, params);
	}

	if (token === CAMERA_ACTION_TOKENS.ZONE) {
		const config = params.config || {};
		return {
			name: CAMERA_ACTION_NAMES.ZONE,
			value: normalizeCameraConditionValue(config.enable),
			channel: 0,
			add_info_ind: String(params.id ?? 0),
		};
	}

	return null;
};
